import { createDevLogger } from 'chimera/all/plugins/devLogger'

const AUTO_CAPTURE_FILTER_KEY = 'statsigHideAutoCapture'

/**
 * Get the hide auto-capture filter setting from localStorage
 * @returns {boolean}
 */
const getHideAutoCapture = () => {
  if (typeof localStorage === 'undefined') {
    return false
  }
  return localStorage.getItem(AUTO_CAPTURE_FILTER_KEY) === 'true'
}

/**
 * Set the hide auto-capture filter setting
 * @param {boolean} hide
 */
const setHideAutoCapture = (hide) => {
  if (typeof localStorage === 'undefined') {
    return
  }
  localStorage.setItem(AUTO_CAPTURE_FILTER_KEY, hide ? 'true' : 'false')
}

/**
 * Statsig DevLogger instance
 * Uses the shared DevLogger utility for consistent logging
 */
const statsigLogger = createDevLogger({
  name: 'Statsig',
  badgeColor: '#6366f1',
  storageKeyPrefix: 'statsig',
  defaultEnabled: true,
})

/**
 * Additional text styles for different Statsig event types
 */
const TEXT_STYLES = {
  event: 'color: #6366f1; font-weight: bold;',
  autoCapture: 'color: #8b5cf6; font-weight: normal;',
  exposure: 'color: #10b981; font-weight: bold;',
  override: 'color: #f59e0b; font-weight: bold;',
  gate: 'color: #64748b; font-weight: bold;',
  warn: 'color: #ef4444; font-weight: bold;',
}

// Re-export with Statsig-specific names for backward compatibility
export const getStatsigLogging = statsigLogger.getEnabled
export const setStatsigLogging = statsigLogger.setEnabled
export const getStatsigLogsExpanded = statsigLogger.getExpanded
export const setStatsigLogsExpanded = statsigLogger.setExpanded
export { getHideAutoCapture, setHideAutoCapture }

/**
 * Check if an event name is an auto-capture event
 * @param {string} eventName
 * @returns {boolean}
 */
const isAutoCaptureEvent = (eventName) => {
  return eventName?.startsWith('auto_capture::')
}

/**
 * Bind development logging event handlers to Statsig client
 * Logs events, experiment evaluations, and gate evaluations to the browser console
 * when logging is enabled
 *
 * @param {object} statsigClient - The Statsig client instance
 */
export function bindDevLogging(statsigClient) {
  // Log custom events sent through Statsig
  statsigClient.on('log_event_called', ({ event }) => {
    // Check if we should filter out auto-capture events
    if (isAutoCaptureEvent(event.eventName) && getHideAutoCapture()) {
      return
    }

    // Use different style for auto-capture events
    const textStyle = isAutoCaptureEvent(event.eventName)
      ? TEXT_STYLES.autoCapture
      : TEXT_STYLES.event

    statsigLogger.log(event.eventName, {
      textStyle,
      data:
        event.metadata && Object.keys(event.metadata).length > 0
          ? event.metadata
          : null,
      fullData:
        event.value !== null && event.value !== undefined
          ? { value: event.value, metadata: event.metadata }
          : null,
    })
  })

  // Log experiment evaluations and assignments
  statsigClient.on('experiment_evaluation', ({ experiment }) => {
    const reason = experiment.details?.reason ?? ''
    const isUnrecognized = reason.includes('Unrecognized')
    const isOverride = reason.includes('LocalOverride')

    const variant = isUnrecognized
      ? '(unknown key)'
      : (experiment.groupName ?? 0)

    const textStyle = isUnrecognized
      ? TEXT_STYLES.warn
      : isOverride
        ? TEXT_STYLES.override
        : TEXT_STYLES.exposure

    statsigLogger.log(`experiment: ${experiment.name} → ${variant}`, {
      textStyle,
      fullData: { reason },
      forceExpand: isUnrecognized,
    })
  })

  // Log feature gate evaluations
  statsigClient.on('gate_evaluation', ({ gate }) => {
    statsigLogger.log(`gate: ${gate.name} → ${gate.value}`, {
      textStyle: TEXT_STYLES.gate,
      fullData: { reason: gate.details?.reason },
    })
  })
}
